import {
    BookmarkBorderedIcon,
    Button,
    Controls,
    ControlsItem,
    ScrollView,
    Typography
} from '@genestack/ui';
import classNames from 'classnames';
import React from 'react';
import {InfiniteData} from '@tanstack/react-query';

import {UsageItemsGroup} from '../../../../../../components/usage-items-group';
import {
    useFindUsagesQuery,
    PAGE_SIZE,
    FindUsagesWithDocNodes,
    FindUsagesResponse
} from '../../../hooks/requests/use-find-usages-query';
import {extractInfiniteQueryItems} from '../../../../../../components/pagination/infinite-query-items';
import {pluralize} from '../../../../../../utils/pluralize';
import {CopySearchButton} from '../../copy-search-button';
import {AppliedFiltersSummary} from '../categories/applied-filters-summary';
import {Categories} from '../categories/categories';
import {ExpandCategoriesButton} from '../categories/expand-button';
import {SearchResultsBody} from '../search-results-body';
import {SearchResultsHeader} from '../search-results-header';

import {ViewModeDocumentNodes} from './document-nodes';
import styles from './node-results.module.css';
import {SearchResultComponentProps} from '../interface';
import {CategoryTreeElement} from '../categories/adapt-tree';
import {NodeSuggestion} from '../../../../common/sidebar-search-field/interface';
import {Link} from 'wouter';

export function NodeResults({
    searchData,
    categoriesFilters,
    versionId,
    expanded,
    onToggleExpand,
    loadingState,
    setLoadingState,
    selectedGlobalVersion,
    getDocumentLink
}: SearchResultComponentProps<NodeSuggestion>) {
    const {nodeId, documentAccession} = searchData;

    const findUsagesQuery = useFindUsagesQuery({
        globalVersionId: versionId,
        accession: documentAccession,
        declarationNodeId: nodeId,
        categories: categoriesFilters.appliedFilters,
        enabled: !!documentAccession && !!nodeId
    });

    const hasFindUsages = Number(findUsagesQuery?.data?.pages[0]?.total) > 0;

    const searchLabel = React.useMemo(
        () => getSearchLabel(nodeId, hasFindUsages, findUsagesQuery?.data),
        [nodeId, findUsagesQuery?.data, hasFindUsages]
    );

    const items = findUsagesQuery
        ? extractInfiniteQueryItems(findUsagesQuery, (result: FindUsagesResponse) => result.data)
        : [];

    const renderFindUsage = React.useCallback(
        (findUsage: FindUsagesWithDocNodes) => {
            const {usages, remaining, nextCursor, document, total} = findUsage;

            const element = (
                <UsageItemsGroup
                    className={styles.usages}
                    type={document.type}
                    label={document.name}
                    readOnly={document.readOnly}
                    count={total}
                    append={
                        document.userFavorite && (
                            <div title="In your Bookmarks">
                                <BookmarkBorderedIcon />
                            </div>
                        )
                    }
                >
                    <ViewModeDocumentNodes
                        usageAccession={document.accession}
                        searchData={searchData}
                        initialNodes={usages}
                        remaining={remaining}
                        cursor={nextCursor}
                        selectedGlobalVersion={selectedGlobalVersion}
                        getDocumentLink={getDocumentLink}
                    />
                </UsageItemsGroup>
            );

            return [findUsage.document.accession, element] as [React.Key, React.ReactElement];
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [findUsagesQuery?.data, searchData]
    );

    const handleChangeFilters = React.useCallback(
        (
            toAddVisual: CategoryTreeElement[],
            toRemoveVisual: CategoryTreeElement[],
            toAddApplied: string[],
            toRemoveApplied: string[]
        ) => {
            categoriesFilters.onChangeAppliedFilters(toAddApplied, toRemoveApplied);
            categoriesFilters.onChangeVisualFilters(toAddVisual, toRemoveVisual);
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []
    );

    React.useEffect(() => {
        categoriesFilters.onResetFilters();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchData]);

    return (
        <>
            <SearchResultsHeader loadingState={loadingState}>
                <Controls justify="space-between">
                    <ControlsItem shrink>
                        <Controls>
                            <ControlsItem>
                                <ExpandCategoriesButton
                                    expanded={expanded}
                                    onToggleExpand={onToggleExpand}
                                />
                            </ControlsItem>
                            <ControlsItem className={styles.label} shrink title={searchLabel}>
                                <Typography variant="section" ellipsis>
                                    {searchLabel}
                                </Typography>
                            </ControlsItem>
                        </Controls>
                    </ControlsItem>

                    {nodeId && (
                        <ControlsItem>
                            <Controls>
                                <ControlsItem>
                                    <Button
                                        ghost
                                        size="small"
                                        component={Link}
                                        to={getDocumentLink(documentAccession!, nodeId)}
                                    >
                                        Go to reference
                                    </Button>
                                </ControlsItem>

                                <ControlsItem>
                                    <CopySearchButton
                                        searchData={searchData}
                                        selectedGlobalVersion={selectedGlobalVersion}
                                    />
                                </ControlsItem>
                            </Controls>
                        </ControlsItem>
                    )}
                </Controls>
            </SearchResultsHeader>

            <div className={styles.main}>
                {expanded && documentAccession && nodeId ? (
                    <ScrollView className={styles.left}>
                        <Categories
                            versionId={versionId}
                            referenceNode={{accession: documentAccession, nodeId}}
                            visualFilters={categoriesFilters.visualFilters}
                            appliedFilters={categoriesFilters.appliedFilters}
                            onChangeFilters={handleChangeFilters}
                        />
                    </ScrollView>
                ) : null}
                <ScrollView className={classNames(styles.right, {[styles.expanded]: expanded})}>
                    {categoriesFilters.visualFilters.length ? (
                        <AppliedFiltersSummary
                            visualFilters={categoriesFilters.visualFilters}
                            onChangeFilters={handleChangeFilters}
                            isLoading={loadingState.isLoading}
                            count={findUsagesQuery?.data?.pages[0]?.total || 0}
                            onResetFilters={categoriesFilters.onResetFilters}
                        />
                    ) : null}
                    <SearchResultsBody
                        isLoading={!versionId}
                        pageSize={
                            findUsagesQuery?.data?.pages.map((page) => page.data.length) ||
                            PAGE_SIZE
                        }
                        items={items}
                        paginationState={{
                            items,
                            total: findUsagesQuery?.data?.pages[0]?.total,
                            isLoading: findUsagesQuery?.isLoading || false,
                            loadNextPage: findUsagesQuery?.fetchNextPage,
                            isLoadingNextPage: findUsagesQuery?.isFetchingNextPage,
                            hasNextPage: findUsagesQuery?.hasNextPage
                        }}
                        renderItem={renderFindUsage}
                        onLoadNextPage={findUsagesQuery?.fetchNextPage}
                        loadingState={loadingState}
                        setLoadingState={setLoadingState}
                    />
                </ScrollView>
            </div>
        </>
    );
}

function getSearchLabel(
    nodeId: string | null,
    hasFindUsages: boolean,
    response?: InfiniteData<FindUsagesResponse>
) {
    if (!nodeId || !hasFindUsages) {
        return 'No results found';
    }

    if (response && response.pages) {
        const {totalNodes, total} = response.pages[0];
        const usagesLabel = `${totalNodes} ${pluralize('usage', totalNodes)}`;
        const documentLabel = `${total} ${pluralize('document', total)}`;

        return `${usagesLabel} in ${documentLabel}`;
    }

    return 'No usages found';
}
